package com.flowyride.core.api

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Level
import com.flowyride.core.domain.model.Maneuver
import com.flowyride.core.domain.model.ManeuverType
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.model.RouteEngineId
import com.flowyride.core.domain.model.RouteLeg
import com.flowyride.core.domain.model.RoutePreferences
import com.flowyride.core.domain.model.RouteRequest
import com.flowyride.core.domain.model.RouteStyle
import com.flowyride.core.domain.model.SpeedLimitSection
import com.flowyride.core.domain.model.TrackPoint
import com.flowyride.core.domain.routing.RoutingException

fun GeoPoint.toDto() = GeoPointDto(lat = latitude, lon = longitude)

fun GeoPointDto.toDomain() = GeoPoint(latitude = lat, longitude = lon)

fun RoutePreferences.toDto() = RoutePreferencesDto(
    windingness = windingness.toDto(),
    hilliness = hilliness.toDto(),
    avoidMotorways = avoidMotorways,
)

fun RoutePreferencesDto.toDomain() = RoutePreferences(
    windingness = windingness.toDomain(),
    hilliness = hilliness.toDomain(),
    avoidMotorways = avoidMotorways,
)

fun RouteRequest.toDto() = RouteRequestDto(
    waypoints = waypoints.map { it.toDto() },
    preferences = preferences.toDto(),
    style = RouteStyleDto.valueOf(style.name),
    heading = initialHeading,
)

/** @throws IllegalArgumentException on invalid coordinates or too few waypoints. */
fun RouteRequestDto.toDomain() = RouteRequest(
    waypoints = waypoints.map { it.toDomain() },
    preferences = preferences.toDomain(),
    style = RouteStyle.valueOf(style.name),
    initialHeading = heading?.takeIf { it.isFinite() },
)

fun Route.toDto() = RouteResponseDto(
    engine = engineId.value,
    distanceMeters = distanceMeters,
    durationSeconds = durationSeconds,
    track = track.map { TrackPointDto(it.point.latitude, it.point.longitude, it.elevationMeters) },
    legs = legs.map { RouteLegDto(it.distanceMeters, it.durationSeconds, it.lastTrackIndex) },
    maneuvers = maneuvers.map { it.toDto() },
    speedLimits = speedLimits.map { it.toDto() },
)

fun Maneuver.toDto() = ManeuverDto(type.name, trackIndex, roadName, roadNumber, roundaboutExit, turnAngle)

/** Unknown kinds (newer server) become OTHER. */
fun ManeuverDto.toDomain() = Maneuver(
    type = ManeuverType.entries.firstOrNull { it.name == type } ?: ManeuverType.OTHER,
    trackIndex = trackIndex,
    roadName = roadName,
    roadNumber = roadNumber,
    roundaboutExit = roundaboutExit,
    turnAngle = turnAngle,
)

fun SpeedLimitSection.toDto() = SpeedLimitSectionDto(startTrackIndex, endTrackIndex, speedLimitKmh)

/** Sections outside of a track of [trackSize] points are dropped. */
fun List<SpeedLimitSectionDto>.toDomain(trackSize: Int): List<SpeedLimitSection> =
    filter { it.startTrackIndex in 0 until trackSize && it.endTrackIndex in it.startTrackIndex until trackSize }
        .map { SpeedLimitSection(it.startTrackIndex, it.endTrackIndex, it.speedLimitKmh) }

/** The server's engine is an implementation detail: the app reports the route as coming from [engineId]. */
fun RouteResponseDto.toDomain(engineId: RouteEngineId) = Route(
    engineId = engineId,
    track = track.map { TrackPoint(GeoPoint(it.lat, it.lon), it.ele) },
    distanceMeters = distanceMeters,
    durationSeconds = durationSeconds,
    legs = legs.map { RouteLeg(it.distanceMeters, it.durationSeconds, it.lastTrackIndex) },
    maneuvers = maneuvers
        // Out-of-range indices would crash the navigation: drop them.
        .filter { it.trackIndex in track.indices }
        .map { it.toDomain() },
    speedLimits = speedLimits.toDomain(track.size),
)

fun RoutingException.toApiError(): ApiErrorDto = when (this) {
    is RoutingException.NoRouteFound -> ApiErrorDto(ApiErrorCode.NO_ROUTE, message.orEmpty())
    is RoutingException.TooManyWaypoints -> ApiErrorDto(ApiErrorCode.TOO_MANY_WAYPOINTS, message.orEmpty())
    is RoutingException.EngineUnavailable -> ApiErrorDto(ApiErrorCode.ENGINE_UNAVAILABLE, message.orEmpty())
    is RoutingException.RateLimited -> ApiErrorDto(ApiErrorCode.RATE_LIMITED, message.orEmpty())
    // Generic on purpose: upstream messages may contain the engine's request URL, API key included.
    is RoutingException.Network -> ApiErrorDto(ApiErrorCode.UPSTREAM_ERROR, "Routing engine unreachable")
    is RoutingException.InvalidResponse -> ApiErrorDto(ApiErrorCode.UPSTREAM_ERROR, "Unexpected answer from the routing engine")
}

fun ApiErrorDto.toRoutingException(maxWaypoints: Int): RoutingException = when (code) {
    ApiErrorCode.NO_ROUTE -> RoutingException.NoRouteFound(message)
    ApiErrorCode.TOO_MANY_WAYPOINTS -> RoutingException.TooManyWaypoints(maxWaypoints)
    ApiErrorCode.INVALID_REQUEST, ApiErrorCode.UPSTREAM_ERROR -> RoutingException.InvalidResponse(message)
    ApiErrorCode.RATE_LIMITED -> RoutingException.RateLimited(message)
    else -> RoutingException.EngineUnavailable(message)
}

private fun Level.toDto() = when (this) {
    Level.LOW -> LevelDto.LOW
    Level.NORMAL -> LevelDto.NORMAL
    Level.HIGH -> LevelDto.HIGH
}

private fun LevelDto.toDomain() = when (this) {
    LevelDto.LOW -> Level.LOW
    LevelDto.NORMAL -> Level.NORMAL
    LevelDto.HIGH -> Level.HIGH
}
